import SlippageToggleInputV2 from "./slippage-toggle-input-v2";
import AutoSellButtonsV2 from "./components/auto-sell-buttons-v2";
import { useBuySellBotTokenContext } from "../../providers/buy-sell-token-provider";

import { useState } from "react";
import BuySellTurboOptions from "./components/buy-sell-turbo-options";
import { formatNativeCurrency, formatTokenCurrency } from "@/utils/currency";
import { BotWalletSupportedChain, useWallet } from "@/providers/wallet";
import { useToken } from "../../hooks/use-token";
import { useBotBuyTokenMutation } from "@/api/bot/bot-buy-token";
import { useQueryClient } from "@tanstack/react-query";
import { BOT_GET_TOKEN_INFO_QUERY_KEY } from "@/api/bot/bot-get-token-info";
import { BOT_GET_USER_INFO_QUERY_KEY } from "@/api/bot/bot-get-user-info";
import { BOT_GET_ORDERS_QUERY_KEY } from "@/api/bot/bot-get-orders";
import { useWeb3Auth } from "@web3auth/modal-react-hooks";
import { SCREENER_TRANSACTIONS_QUERY } from "@/api/token-screener/screener-transactions";
import { SCREENER_QUERY_KEY } from "@/api/token-screener/screener-tokens";
import { POSITIONS_QUERY_KEY } from "@/api/positions/positions-bot";
import { useBotSellTokenMutation } from "@/api/bot/bot-sell-token";
import { TRANSACTIONS_BOT_QUERY_KEY } from "@/api/transactions/transactions-bot";

const TurboBotActions = () => {
  const { isConnected } = useWeb3Auth();

  const { userId, appPubKey, currentWallet } = useWallet();

  const token = useToken();

  const buySellContext = useBuySellBotTokenContext();
  const [isAutoSell, setIsAutoSell] = useState(false);

  const { mutate: buyMutateInstant, isPending: isBuyPendingInstant } =
    useBotBuyTokenMutation();
  const { mutate: sellMutateInstant, isPending: isSellPendingInstant } =
    useBotSellTokenMutation();

  const queryClient = useQueryClient();

  const pairInfo = token?.botPair || { nativePrice: 0, priceInNative: 0 };

  const handleBuy = (amount: string) => {
    const youPayFormattedAmount = formatNativeCurrency(
      amount,
      currentWallet.botChainId,
    );

    if (youPayFormattedAmount === "") return;

    const botChainId = token?.tokenId?.startsWith("0x")
      ? BotWalletSupportedChain.ETH
      : BotWalletSupportedChain.SOL;

    const botChainSymbol =
      botChainId === BotWalletSupportedChain.ETH ? "ETH" : "SOL";

    buyMutateInstant(
      {
        params: {
          userId,
          appPubKey,
          chainId: botChainId,
          tokenAddress: token?.address ?? "",
          walletAddress: currentWallet.address,
          amount: youPayFormattedAmount,
          slippage: buySellContext.isBuySlippageAuto
            ? undefined
            : Number(buySellContext.buySlippage),
          isAutoSellOn: isAutoSell,
        },
        notifyParams: {
          tokenSymbol: token?.symbol,
          youPayNative: amount,
          chainSymbol: botChainSymbol,
          youReceive: String(Number(amount) / pairInfo.priceInNative),
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [BOT_GET_TOKEN_INFO_QUERY_KEY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [BOT_GET_USER_INFO_QUERY_KEY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [SCREENER_TRANSACTIONS_QUERY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [SCREENER_QUERY_KEY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [POSITIONS_QUERY_KEY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [BOT_GET_ORDERS_QUERY_KEY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [TRANSACTIONS_BOT_QUERY_KEY],
            exact: false,
          });
        },
      },
    );
  };

  const handleSell = (percentage: string) => {
    const amount = String((Number(token?.balance) * Number(percentage)) / 100);

    const botChainId = token?.tokenId?.startsWith("0x")
      ? BotWalletSupportedChain.ETH
      : BotWalletSupportedChain.SOL;

    const botChainSymbol =
      botChainId === BotWalletSupportedChain.ETH ? "ETH" : "SOL";

    sellMutateInstant(
      {
        params: {
          userId,
          appPubKey,
          chainId: botChainId,
          tokenAddress: token?.address ?? "",
          amount: token
            ? formatTokenCurrency(amount, token.decimals)
            : formatNativeCurrency(amount, currentWallet.botChainId),
          slippage: buySellContext.isSellSlippageAuto
            ? undefined
            : Number(buySellContext.sellSlippage),
          walletAddress: currentWallet.address,
        },
        notifyParams: {
          youSell: amount,
          tokenSymbol: token?.symbol,
          youReceive: String(Number(amount) * pairInfo.priceInNative),
          chainSymbol: botChainSymbol,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [BOT_GET_TOKEN_INFO_QUERY_KEY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [BOT_GET_USER_INFO_QUERY_KEY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [POSITIONS_QUERY_KEY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [SCREENER_TRANSACTIONS_QUERY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [SCREENER_QUERY_KEY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [BOT_GET_ORDERS_QUERY_KEY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [TRANSACTIONS_BOT_QUERY_KEY],
            exact: false,
          });
        },
      },
    );
  };

  return (
    <div className="flex flex-col mt-[0.938rem]">
      <BuySellTurboOptions
        onBuyTurbo={(amount) => {
          if (isConnected && currentWallet) {
            handleBuy(amount);
          }
        }}
        onSellTurbo={(percentage) => {
          if (isConnected && currentWallet) {
            handleSell(percentage);
          }
        }}
        loading={isBuyPendingInstant || isSellPendingInstant}
      />
      <AutoSellButtonsV2
        isAutoSell={isAutoSell}
        setIsAutoSell={setIsAutoSell}
      />
      <SlippageToggleInputV2
        isAuto={buySellContext.isBuySlippageAuto}
        value={buySellContext.buySlippage}
        onAutoChange={() =>
          buySellContext.setIsBuySlippageAuto((prev) => !prev)
        }
        onValueChange={(v) => buySellContext.setBuySlippage(v)}
      />
    </div>
  );
};

export default TurboBotActions;
